/**
 * src/features/referral/hooks/useReferralData.ts
 *
 * Fetch hook for GET /api/referral/me — manages loading/error/data lifecycle.
 * Consumed by InviteWidget (Plan 02). Fires on component mount (lazy — §10
 * optional complexity: InviteWidget mounts collapsed on Dashboard, hook fires
 * on expand via Plan 02 wiring).
 *
 * Do NOT extend useReferralStore with this data — the store is attribution-only
 * (pendingCode: string | null from Phase 25). This hook manages fetch state independently.
 *
 * T-27-02: GET /api/referral/me returns counts only (no referee PII); referralUrl
 * code is non-secret sharable-by-design.
 * T-27-03: Fetch fires only on component mount (lazy), not eagerly in App.tsx.
 */

import { useState, useEffect } from 'react';
import { z } from 'zod';

// Boundary validation (§7): GET /api/referral/me is an untrusted network boundary.
// A 200 response with a malformed body (e.g. missing `stats`) previously crashed the
// whole Settings/System tab via ErrorBoundary at `stats.total`. Parsing the response
// through these schemas turns a shape mismatch into a graceful error state instead.
export const ReferralStatsSchema = z.object({
  total: z.number(),
  // pending is ALWAYS 0 from API — Phase 24 getReferralStats hardcode;
  // count-based summary uses signed_up/converted only (Phase 27 known limitation).
  pending: z.number(),
  signed_up: z.number(),
  converted: z.number(),
  rewards_granted: z.number(),
});

export const ReferralDataSchema = z.object({
  referralCode: z.string().nullable(),
  referralUrl: z.string().nullable(),
  stats: ReferralStatsSchema,
});

// Types derived from the schemas — single source of truth (no drift between the
// runtime guard and the compile-time shape).
export type ReferralStats = z.infer<typeof ReferralStatsSchema>;
export type ReferralData = z.infer<typeof ReferralDataSchema>;

export interface UseReferralDataResult {
  data: ReferralData | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useReferralData(): UseReferralDataResult {
  const [data, setData] = useState<ReferralData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/referral/me');
      if (!res.ok) {
        throw new Error('fetch failed');
      }
      const json: unknown = await res.json();
      // Validate the untrusted response shape before handing it to the UI (§7).
      // safeParse never throws — a mismatch falls through to the graceful error state.
      const parsed = ReferralDataSchema.safeParse(json);
      if (!parsed.success) {
        throw new Error('invalid shape');
      }
      setData(parsed.data);
    } catch {
      setError('load_failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // Empty deps: intentional — load is a local function, fires once on mount.
  }, []);

  return { data, loading, error, refetch: load };
}
